import { Gift, PackageCheck, Truck, Undo2, X } from "lucide-react";
import { formatIDR } from "@kanata/shared";
import type { CartPromo } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { promoEndsLabel } from "@/lib/promo";

/**
 * The automatic promotion in the cart: what it gives, struck through to Rp 0,
 * and what it saves. The mockup Divine's team drew, as a block under the
 * desks it came with.
 *
 * The gift is not a stored cart line — it follows the desks — so it has its own
 * controls. Taking it out is remembered until checkout; putting it back is one
 * tap. Short stock never blocks anything: the block says so and the rest of
 * the paket still applies.
 */
export function PromoBlock({ promo }: { promo: CartPromo }) {
  const { busy, setPromoGift } = useCart();
  const gift = promo.gift;
  const ends = promoEndsLabel(promo.endsAt);

  return (
    <section
      aria-label={promo.name}
      className="mt-2 rounded-2xl border border-brand/25 bg-brand-light/40 p-5"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium text-brand-dark">{promo.name}</h2>
        {ends && <span className="text-xs text-brand-dark/70">Berlaku sampai {ends}</span>}
      </div>
      {promo.tagline && <p className="mt-1 text-sm text-ink/60">{promo.tagline}</p>}

      <ul className="mt-4 space-y-3 text-sm">
        {gift && (
          <li className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
              {gift.imageUrl ? (
                <img src={gift.imageUrl} alt="" className="size-full object-cover" />
              ) : (
                <Gift className="size-4 text-brand-dark" />
              )}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-ink">
                {gift.productName ?? gift.label}
                {gift.qty > 1 && <span className="text-ink/55"> ×{gift.qty}</span>}
              </span>
              <GiftStatus gift={gift} />
            </span>
            {gift.declined ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => void setPromoGift(true)}
                className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand-dark underline-offset-2 hover:underline disabled:opacity-40"
              >
                <Undo2 className="size-3" />
                Ambil lagi
              </button>
            ) : gift.qty > 0 ? (
              <>
                <Struck was={gift.unitPriceIdr * gift.qty} />
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void setPromoGift(false)}
                  aria-label={`Hapus ${gift.productName ?? gift.label} gratis`}
                  className="shrink-0 text-ink/40 transition-colors hover:text-destructive disabled:opacity-40"
                >
                  <X className="size-4" />
                </button>
              </>
            ) : null}
          </li>
        )}

        {promo.packingKayu && (
          <li className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white">
              <PackageCheck className="size-4 text-brand-dark" />
            </span>
            <span className="min-w-0 flex-1 text-ink">
              Packing kayu
              <span className="text-ink/55">
                {" "}
                · {promo.packingKayu.units} meja
              </span>
            </span>
            <Struck was={promo.packingKayu.totalIdr} />
            {/* Keeps the column aligned with the gift's remove button. */}
            {gift && gift.qty > 0 && !gift.declined && <span className="w-4 shrink-0" />}
          </li>
        )}

        {promo.shippingSubsidyMaxIdr > 0 && (
          <li className="flex items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white">
              <Truck className="size-4 text-brand-dark" />
            </span>
            <span className="min-w-0 flex-1 text-ink">
              Subsidi ongkir s/d {formatIDR(promo.shippingSubsidyMaxIdr)}
              <span className="block text-xs text-ink/55">Dipotong dari ongkir kurir yang kamu pilih saat checkout.</span>
            </span>
          </li>
        )}
      </ul>

      {promo.savingIdr > 0 && (
        <p className="mt-4 border-t border-brand/15 pt-3 text-right text-sm font-medium tracking-wide text-brand-dark">
          HEMAT {formatIDR(promo.savingIdr)}
          {promo.shippingSubsidyMaxIdr > 0 && <span className="font-normal text-brand-dark/70"> + subsidi ongkir</span>}
        </p>
      )}
    </section>
  );
}

function Struck({ was }: { was: number }) {
  return (
    <span className="shrink-0 text-right tabular-nums">
      <span className="sr-only">Harga normal </span>
      <del className="text-xs text-ink/45">{formatIDR(was)}</del>{" "}
      <span className="font-medium text-brand-dark">Rp 0</span>
    </span>
  );
}

function GiftStatus({ gift }: { gift: NonNullable<CartPromo["gift"]> }) {
  if (gift.declined) return <span className="block text-xs text-ink/55">Tidak diambil</span>;
  if (gift.qty === 0) {
    return <span className="block text-xs text-ink/55">Sedang habis. Paket lainnya tetap berlaku.</span>;
  }
  if (gift.short) {
    return (
      <span className="block text-xs text-ink/55">
        Tersedia {gift.qty} dari {gift.wanted}. Sisanya sedang habis.
      </span>
    );
  }
  return <span className="block text-xs text-ink/55">Gratis, 1 per meja</span>;
}
